import {NICKNAME_MAX_LENGTH} from '../../common/constant';
import {IRegion} from '../../common/type';
import {
  getTimeStamp,
  formatFromServerDate,
  DEFAULT_DATE_FORMAT,
} from '../../common/util/date';
import {LOGIN_METHOD_TEXT} from '../common';
import {
  IUser,
  UserState,
  ILoginMethod,
  IApiParamsAuthResult,
  NextAuthStep,
  UserStatus,
  SignupType,
  ILastLoginAuth,
  IGlobalLoginMethod,
  ITermsItem,
} from '../type';

export function getUser(data: I_User, accessToken: string): IUser {
  const oauths = data.loginMethodInfos;
  const nickname = data.userNick.substr(0, NICKNAME_MAX_LENGTH);
  const currentLoginMethod = data.loginMethod as ILoginMethod;

  return {
    state: UserState.Login,
    id: data.userId,
    accessToken,
    nickname,
    csId: data.csId,
    profileUrl: data.userProfileUrl || '',
    joinMethod: data.joinMethod as ILoginMethod,
    currentLoginMethod,
    email:
      oauths?.[0].email ||
      `${LOGIN_METHOD_TEXT[currentLoginMethod]} · ${oauths?.[0].name}`,
    authList: oauths
      ? oauths.map(item => ({
          nickname: item.name,
          loginMethod: item.loginMethod as ILoginMethod,
        }))
      : undefined,
    redeemCode: data.redeemCode,
    userStatus: data.userStatus as UserStatus,
    createDt: getTimeStamp(data.created),
    blockedAt:
      data.restrictionInfo?.blockDate &&
      formatFromServerDate(data.restrictionInfo.blockDate, DEFAULT_DATE_FORMAT),
    isIdentityVerified: data.identityVerified,
    region: data.region as IRegion,
    providerId: data.providerId,
    isAdult: data.isAdult,
    isChild: data.isChild,
    isThirdAdAgree: data.acceptThirdPartyPrivacyUsage,
    kakaoAppUserId: oauths?.find(auth => auth.loginMethod === 'KA')?.appUserId,
  };
}
export interface I_User {
  created: string;
  verified: boolean;
  joinMethod: string;
  loginMethod: string;
  loginMethodInfos?: Array<{
    created: string;
    email: string;
    imageUrl: string;
    loginMethod: string;
    name: string;
    appUserId?: string;
  }>;
  sessions: null;
  userId: string;
  csId: string;
  userNick: string;
  userProfileUrl: null | string;
  userStatus: string;
  registeredDeviceId?: string;
  registeredDeviceName?: string;
  registeredOs?: string;
  deviceChangeDate?: string;
  deviceChangeCount: number;
  deviceChangeLimit: number;
  redeemCode: string;
  restrictionInfo?: {
    blockDate: string;
  };
  identityVerified: boolean;
  region: string;
  providerId?: string;
  isAdult: boolean;
  isChild: boolean;
  acceptThirdPartyPrivacyUsage: boolean;
}

export function getApiAuthResult(
  data: I_ApiAuthResult,
  needReload?: boolean
): IApiParamsAuthResult {
  const authStatus = data.authStatus as NextAuthStep | undefined;
  return {
    sessionId: data.sessionId,
    signupType: data.signupType as SignupType | undefined,
    currentStatus: data.signupStatus,
    authStatus,
    isSignUp: data.isSignUp,
    invalidate: authStatus === NextAuthStep.NeedEvictionConfirmation,
    sessionRegion: data.sessionRegion
      ? (data.sessionRegion as IRegion)
      : undefined,
    needReload,
    verificationSessionId: data.verificationSessionId,
  };
}
export interface I_ApiAuthResult {
  sessionId: string;
  signupType: string;
  signupStatus: string;
  authStatus?: string;
  accessToken?: string;
  userInfo?: I_User;
  isSignUp?: boolean;
  isLogin?: boolean;
  sessionRegion?: string;
  verificationSessionId?: string;
}

export function getLastLoginAuth(
  data: I_LastLoginAuth
): ILastLoginAuth | undefined {
  // 테스트 환경을 위한 방어 코드,
  // 국내 카카오 로그인 후 => 리전 변경 => KA가 넘어옴 (KA는 LastLogin처리하지 않음)
  if (data.loginMethod === 'KA') {
    return undefined;
  }
  const loginMethod = data.loginMethod as IGlobalLoginMethod;
  return {
    emailOrName: loginMethod === 'FB' ? data.name : data.email,
    loginMethod,
  };
}
export interface I_LastLoginAuth {
  name: string;
  email: string;
  loginMethod: string;
}

export function getTermsList(data: I_TermsItem[]): ITermsItem[] {
  return data
    .filter(item => !item.appOnly)
    .map(item => ({
      id: item.id,
      detailPath: item.detailPath,
      isRequired: item.required,
      title: item.title,
      version: item.version,
      // 아래 두 값은 사용하지 않는 값 (연령 체크가 필요하면 사용해야함)
      // ageCheck: !!item.ageCheck,
      // minAge: item.minAge || 0,
    }));
}

export interface I_TermsItem {
  id: number;
  messageKey: string;
  detailPath?: string;
  appOnly: boolean;
  required: boolean;
  version: string;
  title: string;
  ageCheck?: boolean;
  minAge?: number;
}
